import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Download as DownloadIcon,
    Print as PrintIcon,
} from '@mui/icons-material';
import {
    buildClientSheet,
    buildClientSheetCsv,
    clientSheetFileName,
    type ClientSheetClient,
    type ClientSheetContract,
} from '../domain/contracts/clientSheet';
import { useDeferredQuery } from '../hooks/useQuery';
import { downloadBlob } from '../utils/downloadBlob';

interface ClientSheetViewProps {
    clientId: number;
}

/**
 * Print stylesheet: only the sheet is printed. The menu, the header and the buttons are hidden,
 * and the scrolling containers of the application shell are released so a long list of
 * contracts runs over as many pages as it needs instead of being cut at the window height.
 */
const PRINT_STYLES = `
@media print {
    body * {
        visibility: hidden;
    }
    .client-sheet-printable,
    .client-sheet-printable * {
        visibility: visible;
    }
    .client-sheet-printable {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        box-shadow: none !important;
        border: none !important;
    }
    .client-sheet-printable thead {
        display: table-header-group;
    }
    .client-sheet-printable tr {
        break-inside: avoid;
    }
}
`;

const ClientSheetView = ({ clientId }: ClientSheetViewProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const clientQuery = useDeferredQuery<{ client: ClientSheetClient }>({
        path: `/clients/${clientId}`,
        method: 'GET',
        shouldExecute: true,
    });
    const contractsQuery = useDeferredQuery<{ contracts: ClientSheetContract[] }>({
        path: '/contracts',
        method: 'GET',
        params: { clnr: clientId, limit: 10000 },
        shouldExecute: true,
    });

    const client = clientQuery.data?.client;
    const contracts = contractsQuery.data?.contracts;
    const failed = clientQuery.error ?? contractsQuery.error;

    const sheet = useMemo(
        () => (client && contracts ? buildClientSheet(client, contracts, key => t(key)) : null),
        [client, contracts, t],
    );

    const exportCsv = () => {
        if (!client || !sheet) {
            return;
        }
        const csv = buildClientSheetCsv(sheet, t('contracts.clientSheet.observations'));
        downloadBlob(
            new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }),
            clientSheetFileName(client),
        );
    };

    let content: ReactNode;
    if (failed) {
        content = <Alert severity="error">{t('contracts.clientSheet.loadError')}</Alert>;
    } else if (!sheet) {
        content = (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress aria-label={t('contracts.clientSheet.loading')} />
            </Box>
        );
    } else {
        content = (
            <Paper
                className="client-sheet-printable"
                data-testid="client-sheet"
                variant="outlined"
                sx={{ p: 3, backgroundColor: '#fff' }}
            >
                <Typography
                    variant="h5"
                    component="h1"
                    data-testid="client-sheet-title"
                    sx={{ fontWeight: 700, fontStyle: 'italic', color: 'primary.main', mb: 0.5 }}
                >
                    {t('contracts.clientSheet.title')}
                </Typography>
                <Typography
                    variant="h6"
                    component="p"
                    data-testid="client-sheet-name"
                    sx={{ fontWeight: 700, mb: 1 }}
                >
                    {sheet.clientName}
                </Typography>

                <Box
                    data-testid="client-sheet-details"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                        gap: 1,
                        mb: 1.5,
                    }}
                >
                    {sheet.details.map(detail => (
                        <Box key={detail.key}>
                            <Typography
                                variant="caption"
                                component="div"
                                color="text.secondary"
                                sx={{ lineHeight: 1.2 }}
                            >
                                {detail.label}
                            </Typography>
                            <Typography
                                variant="body2"
                                component="div"
                                data-testid={`client-sheet-detail-${detail.key}`}
                                sx={{ fontWeight: 600 }}
                            >
                                {detail.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Typography variant="caption" component="div" color="text.secondary">
                    {t('contracts.clientSheet.observations')}
                </Typography>
                <Typography
                    variant="body2"
                    data-testid="client-sheet-observations"
                    sx={{ whiteSpace: 'pre-wrap', mb: 2, minHeight: '1.5em' }}
                >
                    {sheet.observations || '-'}
                </Typography>

                <Table size="small" data-testid="client-sheet-contracts">
                    <TableHead>
                        <TableRow>
                            {sheet.columns.map(column => (
                                <TableCell
                                    key={column.key}
                                    align={column.align}
                                    sx={{ fontWeight: 700 }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sheet.rows.map(row => (
                            <TableRow key={row.key} data-testid="client-sheet-row">
                                {row.cells.map((cell, index) => (
                                    <TableCell
                                        key={sheet.columns[index].key}
                                        align={sheet.columns[index].align}
                                    >
                                        {cell}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {sheet.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={sheet.columns.length}>
                                    {t('contracts.clientSheet.noContracts')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                <Box
                    data-testid="client-sheet-totals"
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 3,
                        mt: 2,
                        pt: 1.5,
                        borderTop: 1,
                        borderColor: 'divider',
                    }}
                >
                    {sheet.totals.map(total => (
                        <Box key={total.key}>
                            <Typography
                                variant="caption"
                                component="div"
                                color="text.secondary"
                                sx={{ lineHeight: 1.2 }}
                            >
                                {total.label}
                            </Typography>
                            <Typography
                                variant="body1"
                                component="div"
                                data-testid={`client-sheet-total-${total.key}`}
                                sx={{ fontWeight: 700 }}
                            >
                                {total.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Paper>
        );
    }

    return (
        <Box data-testid="client-sheet-page">
            <style>{PRINT_STYLES}</style>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} className="client-sheet-toolbar">
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<BackIcon />}
                    onClick={() => navigate(`/contracts?clientId=${clientId}`)}
                >
                    {t('contracts.clientSheet.back')}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PrintIcon />}
                    data-testid="client-sheet-print"
                    disabled={!sheet}
                    onClick={() => window.print()}
                >
                    {t('contracts.clientSheet.print')}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    data-testid="client-sheet-export"
                    disabled={!sheet}
                    onClick={exportCsv}
                >
                    {t('contracts.clientSheet.export')}
                </Button>
            </Stack>
            {content}
        </Box>
    );
};

/** Reads the client from `?clientId=` and gives each client a view of their own. */
const ClientSheet = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const clientId = Number(searchParams.get('clientId'));

    if (!Number.isInteger(clientId) || clientId <= 0) {
        return <Alert severity="warning">{t('contracts.clientSheet.noClient')}</Alert>;
    }

    return <ClientSheetView key={clientId} clientId={clientId} />;
};

export default ClientSheet;
